import React, { useState, useEffect, useContext, useCallback } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { ApiContext, AuthContext } from '../../../App';
import CTAButton from '../../shared/CTAButton';
import logo from '../../../lib/images/logo.svg';
import Support from '../../shared/Support';
import { useNavigate } from 'react-router';
import { createLinkToken } from '../../../lib/actions/plaid';

export default function Home() {
  const api = useContext(ApiContext);
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);

  const user = auth?.auth?.user;

  const onSuccess = useCallback(async (publicToken) => {
    setLoading(true);
    await api
      .post('/api/plaid/set_access_token', { public_token: publicToken })
      .then(() => {
        setLoading(false);
        navigate('/success');
      });
  }, []);

  const onExit = useCallback(async (error) => {
    if (error) navigate('/fail');
    // to handle other error codes, see https://plaid.com/docs/errors/
  }, []);

  // Creates a Link token
  const createToken = useCallback(async () => {
    createLinkToken(api)
      .then((res) => {
        localStorage.setItem('link_token', res);
        setToken(res);
      })
      .catch((err) => {
        if (err.code === 401) navigate('/login');
      });
  }, [setToken]);

  const config = {
    token,
    onSuccess,
    onExit,
  };

  const { open, ready } = usePlaidLink(config);

  useEffect(() => {
    if (!token && user) {
      createToken();
    }
  }, [user]);

  return (
    <div id="content" className="max-w-xl mx-auto">
      <p className="text-primary-blue text-2xl text-center">
        <FontAwesomeIcon
          icon={['fal', 'building-columns']}
          className="rounded-full border border-[#E9EDF2] p-3"
        />
      </p>
      <div className="text-2xl text-center my-2 font-avenir-medium">
        Connect your bank with
        <span className="text-primary-blue ml-1">Plaid</span>
      </div>

      <div className="text-center mb-4">
        <p>
          Verify your assets and transactions to request a line increase and
          with Credit Key. <br />
          It takes less than 5 minutes.
        </p>
        <p className="my-6 text-center font-avenir-medium">
          If you prefer to manually upload bank statements
          <a
            href="https://www.creditkey.com/credit-key-increase-request"
            target="_blank"
            rel="noreferrer"
            className="ml-1 underline text-primary-blue"
          >
            click here
          </a>
          .
        </p>
      </div>

      <div className="flex items-start px-12 pb-3 pt-4 mb-2 border-t border-[#E9EDF2]">
        <span className="fa-layers fa-fw text-2xl text-primary-blue">
          <FontAwesomeIcon icon={['far', 'badge-check']} />
        </span>
        <div className="mx-5">
          <p className="font-avenir-black">Instant Verification</p>
          <p className="text-sm">
            Credit Key uses Plaid to verify your bank account and check your
            transaction history to ensure you are authorized for a line increase
          </p>
        </div>
      </div>
      <div className="flex items-start px-12 pb-3 pt-4 mb-2 border-t border-[#E9EDF2]">
        <span className="fa-layers fa-fw text-2xl text-primary-blue">
          <FontAwesomeIcon icon={['far', 'lock-keyhole']} />
        </span>
        <div className="mx-3">
          <p className="font-avenir-black">Trusted and Secure</p>
          <p className="text-sm">
            Plaid secures your information with bank-level 256-bit encryption.
            <br />
            Credit Key never stores your credentials.
          </p>
        </div>
      </div>
      <CTAButton
        ariaLabel="link-button"
        buttonClass="w-3/5 bg-primary-blue text-white rounded-full py-3 px-6 cursor-pointer text-sm md:text-base inline-block hover:bg-navy-blue"
        containerClass="my-4 mx-auto justify-center"
        isLoading={loading}
        onClick={() => open()}
        disabled={!ready}
        buttonText="Connect Bank Account"
      />
      <div className="mt-8 text-center text-dark-gray">
        <Support fullText={true} />
      </div>
    </div>
  );
}
